import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, subMonths } from 'date-fns';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { PACKAGING_USAGE, SHIPHERO_LABEL_COST } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';

const PAGE_SIZE = 100;

const PACKAGING_USAGE_DISPLAY_COLUMNS = [
  { key: 'order', label: 'Order', width: '8%' },
  { key: 'tracking', label: 'Tracking', width: '12%' },
  { key: 'box', label: 'Box', width: '11%' },
  { key: 'order_skus', label: 'Order SKUs', width: '18%' },
  { key: 'physical_value', label: 'Physical', width: '10%', type: 'currency' },
  { key: 'covered_in_cogs_value', label: 'Covered in COGS', width: '11%', type: 'currency' },
  { key: 'additional_expense_value', label: 'Additional Expense', width: '11%', type: 'currency' },
  { key: 'unresolved_value', label: 'Held for Review', width: '10%', type: 'currency' },
  { key: 'reason', label: 'Reason', width: '9%' },
];

const REPORTS = {
  [SHIPHERO_LABEL_COST]: {
    title: 'ShipHero Label Cost (Monthly)',
    description: 'One priced label per order shipped in the month (Pacific time). $0 extra-box labels count as packages, not cost.',
    statuses: ['priced', 'missing_cost', 'zero_cost'],
    cards: [['label_cost', 'Label cost', 'currency'], ['orders', 'Orders', 'number'], ['packages', 'Packages', 'number'], ['exceptions', 'Exceptions', 'number']],
    empty: 'No ShipHero labels in the selected month.',
  },
  [PACKAGING_USAGE]: {
    title: 'Packaging Usage (Monthly)',
    description: 'Per-label packaging reconciliation showing physical value, what is already covered in COGS, additional expense, and value held for review.',
    statuses: null,
    dateFormat: 'MM/dd/yyyy',
    cards: [
      ['physical_value', 'Physical Packaging Value', 'currency'],
      ['covered_in_cogs_value', 'Covered in COGS', 'currency'],
      ['additional_expense_value', 'Additional Expense', 'currency'],
      ['unresolved_value', 'Held for Review', 'currency'],
    ],
    empty: 'No boxes shipped in the selected month.',
  },
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const formatValue = (value, type, dateFormat = 'EEEE, MMMM do, yyyy') => {
  if (value === null || value === undefined || value === '') return '—';
  if (type === 'currency') return Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  if (type === 'number') return Number(value).toLocaleString('en-US');
  if (type === 'date') {
    const parsed = parseISO(String(value).slice(0, 10));
    return Number.isNaN(parsed.getTime()) ? value : format(parsed, dateFormat);
  }
  return value;
};

const ShipheroMonthlyCostReport = ({ reportId, marginFromSidebar }) => {
  const config = REPORTS[reportId];
  const navigate = useNavigate();
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);
  const [month, setMonth] = useState(format(subMonths(new Date(), 1), 'yyyy-MM'));
  const [status, setStatus] = useState('all');
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [summaryColumns, setSummaryColumns] = useState([]);
  const [totals, setTotals] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const params = useCallback((includePage = true) => {
    const query = new URLSearchParams({ month });
    if (includePage) {
      query.set('page', page);
      query.set('pageSize', PAGE_SIZE);
    }
    if (status !== 'all') query.set('status', status);
    return query;
  }, [month, page, status]);

  const load = useCallback(async () => {
    const { signal, requestId } = beginRequest();
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/v1/reports/${reportId}?${params()}`, { headers: authHeaders(), signal });
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Failed to load report');
      if (!isLatest(requestId)) return;
      setData(json.data || []);
      setColumns(json.columns || []);
      setSummaryData(json.summaryData || []);
      setSummaryColumns(json.summaryColumns || []);
      setTotals(json.totals || {});
      setTotalRecords(json.totalRecords || 0);
      setTotalPages(json.totalPages || 1);
    } catch (requestError) {
      if (requestError?.name === 'AbortError' || !isLatest(requestId)) return;
      setError(requestError.message);
      setData([]);
      setSummaryData([]);
    } finally {
      if (isLatest(requestId)) setLoading(false);
    }
  }, [beginRequest, isLatest, params, reportId]);

  useEffect(() => {
    if (initialReady) load();
  }, [initialReady, load]);

  useEffect(() => setPage(1), [month, status, reportId]);

  const exportData = async () => {
    const response = await fetch(`/api/v1/reports/${reportId}/export?${params(false)}`, { headers: authHeaders() });
    if (!response.ok) throw new Error('Failed to export report');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportId}-${month}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    return { handled: true };
  };

  const filters = (
    <div className="contents">
      <label className="flex items-center gap-2 text-sm">
        <span>Month</span>
        <input type="month" className="rounded border px-3 py-2 text-sm" value={month} onChange={(event) => event.target.value && setMonth(event.target.value)} />
      </label>
      {config.statuses && (
        <select className="rounded border px-3 py-2 text-sm" value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="all">All statuses</option>
          {config.statuses.map((value) => <option key={value} value={value}>{value}</option>)}
        </select>
      )}
    </div>
  );

  const pagination = (
    <div className="flex items-center justify-between text-sm">
      <span>{totalRecords.toLocaleString()} rows</span>
      <div className="flex gap-2">
        <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded border px-3 py-1 disabled:opacity-50">Previous</button>
        <span className="py-1">Page {page} of {totalPages}</span>
        <button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)} className="rounded border px-3 py-1 disabled:opacity-50">Next</button>
      </div>
    </div>
  );

  const formatCell = (row, column) => {
    if (column.key === 'unresolved_reason' && row.unresolved_reason_display) {
      return row.unresolved_reason_display;
    }
    return formatValue(row[column.key], column.type, config.dateFormat);
  };

  return (
    <ReportTemplate
      reportId={reportId}
      title={config.title}
      description={config.description}
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      isLoading={loading}
      error={error}
      filters={filters}
      exportButton={<CSVExportButton data={data} columns={columns} filename={`${reportId}-${month}`} onExport={exportData} />}
      pagination={pagination}
      renderChildrenOnly
    >
      <div className="space-y-3">
        {reportId === PACKAGING_USAGE && Number(totals.unresolved_value) > 0 && (
          <p className="text-sm font-medium text-amber-800">
            Expense total excludes {formatValue(totals.unresolved_value, 'currency')} held for review
          </p>
        )}
        <div className="grid gap-3 rounded border bg-white p-4 md:grid-cols-4">
          {config.cards.map(([key, label, type]) => (
            <div key={key} className="rounded bg-gray-50 p-3">
              <div className="text-xs text-gray-500">{label}</div>
              <div className="font-semibold">{formatValue(totals[key], type, config.dateFormat)}</div>
            </div>
          ))}
        </div>
        {reportId === PACKAGING_USAGE && summaryData.length > 0 && (
          <div className="rounded border bg-white">
            <h3 className="px-3 py-2 text-sm font-semibold">By box type</h3>
            <div className="overflow-x-clip">
              <table className="w-full table-fixed text-left text-xs">
                <thead className="sticky z-[5] bg-gray-100" style={{ top: 'var(--kl-report-sticky-top, 0px)' }}><tr>{summaryColumns.map((column) => <th key={column.key} className={`break-words px-3 py-2 ${column.type === 'currency' ? 'text-right' : ''}`}>{column.label}</th>)}</tr></thead>
                <tbody>{summaryData.map((row, index) => <tr key={row.box_name} className={index % 2 ? 'bg-gray-50' : ''}>
                  {summaryColumns.map((column) => <td key={column.key} className={`break-words px-3 py-2 ${column.type === 'currency' ? 'text-right whitespace-nowrap' : ''}`}>{formatCell(row, column)}</td>)}
                </tr>)}</tbody>
              </table>
            </div>
          </div>
        )}
        {Array.isArray(totals.by_carrier) && totals.by_carrier.length > 0 && (
          <div className="grid gap-3 rounded border bg-white p-4 md:grid-cols-4">
            {totals.by_carrier.map((row) => (
              <div key={row.carrier} className="rounded bg-gray-50 p-3">
                <div className="text-xs text-gray-500">{row.carrier} ({formatValue(row.orders, 'number')} orders)</div>
                <div className="font-semibold">{formatValue(row.label_cost, 'currency')}</div>
              </div>
            ))}
          </div>
        )}
        <div className="overflow-x-clip rounded border bg-white">
          {data.length === 0 ? <div className="px-6 py-12 text-center text-sm text-gray-500">{config.empty}</div> : (
            <table className="w-full table-fixed text-left text-xs">
              {reportId === PACKAGING_USAGE && (
                <colgroup>
                  {PACKAGING_USAGE_DISPLAY_COLUMNS.map((column) => <col key={column.key} style={{ width: column.width }} />)}
                </colgroup>
              )}
              <thead className="sticky z-[5] bg-gray-100" style={{ top: 'var(--kl-report-sticky-top, 0px)' }}>
                <tr>
                  {(reportId === PACKAGING_USAGE ? PACKAGING_USAGE_DISPLAY_COLUMNS : columns).map((column) => (
                    <th
                      key={column.key}
                      className={`break-words ${reportId === PACKAGING_USAGE ? 'px-2 py-2' : 'px-3 py-2'} ${reportId === PACKAGING_USAGE && column.type === 'currency' ? 'text-right' : ''}`}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>{data.map((row, index) => <tr key={`${row.shiphero_label_id || row.order_number || row.box_name}-${index}`} className={index % 2 ? 'bg-gray-50' : ''}>
                {reportId === PACKAGING_USAGE ? (
                  PACKAGING_USAGE_DISPLAY_COLUMNS.map((column) => {
                    const cellClass = 'px-2 py-2 align-top';
                    if (column.type === 'currency') {
                      return (
                        <td key={column.key} className={`${cellClass} text-right whitespace-nowrap`}>
                          {formatValue(row[column.key], 'currency')}
                        </td>
                      );
                    }
                    if (column.key === 'order') {
                      return (
                        <td key={column.key} className={cellClass}>
                          <div className="whitespace-nowrap">{formatValue(row.order_number, 'string')}</div>
                          <div className="whitespace-nowrap text-[11px] text-gray-500">
                            {formatValue(row.ship_date, 'date', config.dateFormat)}
                          </div>
                        </td>
                      );
                    }
                    if (column.key === 'tracking') {
                      return (
                        <td key={column.key} className={cellClass}>
                          <span className="break-all text-[11px]">{formatValue(row.tracking_number, 'string')}</span>
                        </td>
                      );
                    }
                    if (column.key === 'box') {
                      return (
                        <td key={column.key} className={`${cellClass} break-words`}>
                          <div>{formatValue(row.box_name, 'string')}</div>
                          {row.box_sku && row.box_sku !== row.box_name && (
                            <div className="text-[11px] text-gray-500">{row.box_sku}</div>
                          )}
                        </td>
                      );
                    }
                    if (column.key === 'order_skus') {
                      const skus = String(row.order_skus || '').split(', ').filter(Boolean);
                      return (
                        <td key={column.key} className={`${cellClass} break-words`}>
                          {skus.length > 0 ? skus.map((sku, skuIndex) => (
                            <React.Fragment key={`${sku}-${skuIndex}`}>
                              {skuIndex > 0 && ', '}
                              <span className="inline-block whitespace-nowrap">{sku}</span>
                            </React.Fragment>
                          )) : '—'}
                        </td>
                      );
                    }
                    const reason = row.unresolved_reason_display || row.cost_note || '—';
                    return (
                      <td key={column.key} className={`${cellClass} break-words`}>
                        {reason}
                      </td>
                    );
                  })
                ) : (
                  columns.map((column) => <td key={column.key} className="break-words px-3 py-2">{formatCell(row, column)}</td>)
                )}
              </tr>)}</tbody>
            </table>
          )}
        </div>
      </div>
    </ReportTemplate>
  );
};

export const ShipheroLabelCostReport = (props) => <ShipheroMonthlyCostReport {...props} reportId={SHIPHERO_LABEL_COST} />;
export const PackagingUsageReport = (props) => <ShipheroMonthlyCostReport {...props} reportId={PACKAGING_USAGE} />;

export default ShipheroMonthlyCostReport;
